import { useState } from "react";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Download, FileDown, TrendingUp, TrendingDown, PiggyBank } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatRupiah, formatDateShort, todayStr } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const PIE_COLORS = ["#2563EB", "#06B6D4", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const presetRange = (preset) => {
  const now = new Date();
  const end = todayStr();
  let start = firstOfMonth();
  if (preset === "today") start = end;
  else if (preset === "week") { const d = new Date(); d.setDate(d.getDate() - 7); start = d.toISOString().slice(0, 10); }
  else if (preset === "month") start = firstOfMonth();
  else if (preset === "year") start = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10);
  return { start, end };
};

const toCSV = (rows, headers, filename) => {
  const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const Reports = () => {
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(presetRange("month"));
  const [tab, setTab] = useState("sales");
  const [sales, setSales] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [pl, setPl] = useState(null);
  const [loading, setLoading] = useState(false);

  const applyPreset = (p) => { setPreset(p); if (p !== "custom") setRange(presetRange(p)); };

  const run = async () => {
    setLoading(true);
    try {
      const params = { start: range.start, end: range.end };
      const [s, e, p] = await Promise.all([
        api.get("/reports/sales", { params }),
        api.get("/reports/expenses", { params }),
        api.get("/reports/profit-loss", { params }),
      ]);
      setSales(s.data); setExpenses(e.data); setPl(p.data);
      toast.success("Laporan dimuat");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Laporan" subtitle="Analisis penjualan, pengeluaran, dan laba rugi" />

      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-end">
        <div className="space-y-2">
          <Label className="text-xs">Periode</Label>
          <Select value={preset} onValueChange={applyPreset}>
            <SelectTrigger className="w-full sm:w-40" data-testid="report-preset"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Hari Ini</SelectItem>
              <SelectItem value="week">7 Hari</SelectItem>
              <SelectItem value="month">Bulan Ini</SelectItem>
              <SelectItem value="year">Tahun Ini</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2"><Label className="text-xs">Dari</Label><Input type="date" value={range.start} onChange={(e) => { setPreset("custom"); setRange({ ...range, start: e.target.value }); }} data-testid="report-start" /></div>
        <div className="space-y-2"><Label className="text-xs">Sampai</Label><Input type="date" value={range.end} onChange={(e) => { setPreset("custom"); setRange({ ...range, end: e.target.value }); }} data-testid="report-end" /></div>
        <Button onClick={run} disabled={loading} data-testid="run-report-btn">{loading ? "Memuat..." : "Tampilkan"}</Button>
      </div>

      {!pl ? (
        <div className="rounded-xl border border-dashed border-border bg-card/50 p-16 text-center text-sm text-muted-foreground">
          Pilih periode lalu klik <span className="font-medium text-foreground">Tampilkan</span> untuk melihat laporan.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <StatCard testId="report-revenue" title="Pendapatan" value={formatRupiah(pl.revenue)} icon={TrendingUp} accent="success" hint={`Invoice ${formatRupiah(pl.sales_revenue)} + Lain ${formatRupiah(pl.other_income)}`} />
            <StatCard testId="report-expense" title="Pengeluaran" value={formatRupiah(pl.expense)} icon={TrendingDown} accent="danger" />
            <StatCard testId="report-profit" title="Laba / Rugi" value={formatRupiah(pl.profit)} icon={PiggyBank} accent={pl.profit >= 0 ? "primary" : "warning"} />
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="sales" data-testid="tab-sales">Penjualan</TabsTrigger>
              <TabsTrigger value="expenses" data-testid="tab-expenses">Pengeluaran</TabsTrigger>
            </TabsList>

            <TabsContent value="sales" className="space-y-4">
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={() => toCSV(sales.rows.map((r) => [r.number, r.customer_name, r.date, r.status, r.grand_total]), ["No Invoice", "Customer", "Tanggal", "Status", "Total"], "laporan-penjualan.csv")} data-testid="export-sales-btn">
                  <FileDown className="mr-2 h-4 w-4" />Export Excel/CSV
                </Button>
              </div>
              <div className="rounded-xl border border-border bg-card p-2">
                {sales.rows.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">Tidak ada data pada periode ini</p> : (
                  <Table>
                    <TableHeader><TableRow><TableHead>No Invoice</TableHead><TableHead>Customer</TableHead><TableHead>Tanggal</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {sales.rows.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="font-mono text-xs">{r.number}</TableCell>
                          <TableCell>{r.customer_name}</TableCell>
                          <TableCell>{formatDateShort(r.date)}</TableCell>
                          <TableCell><StatusBadge status={r.status} /></TableCell>
                          <TableCell className="text-right font-medium">{formatRupiah(r.grand_total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </TabsContent>

            <TabsContent value="expenses" className="space-y-4">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="rounded-xl border border-border bg-card p-6">
                  <h3 className="mb-4 font-heading font-semibold">Pengeluaran per Kategori</h3>
                  {expenses.by_category.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">Tidak ada data</p> : (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={expenses.by_category} dataKey="total" nameKey="category" cx="50%" cy="50%" outerRadius={100} label={(e) => e.category}>
                          {expenses.by_category.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => formatRupiah(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
                <div className="rounded-xl border border-border bg-card p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="font-heading font-semibold">Rincian</h3>
                    <Button variant="outline" size="sm" onClick={() => toCSV(expenses.rows.map((r) => [r.name, r.category, r.date, r.nominal]), ["Nama", "Kategori", "Tanggal", "Nominal"], "laporan-pengeluaran.csv")} data-testid="export-expense-btn">
                      <Download className="mr-2 h-4 w-4" />CSV
                    </Button>
                  </div>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={expenses.by_category}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="category" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis hide />
                      <Tooltip formatter={(v) => formatRupiah(v)} cursor={{ fill: "hsl(var(--secondary))" }} />
                      <Bar dataKey="total" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default Reports;
